/**
 * Defined-language regex library for vendor quote PDF extraction
 * (SCM pack — SCM8 tier 1). Mirrors `ppaPatterns.ts` for the PPA
 * term-sheet extractor.
 *
 * Patterns target stable layouts from common vendor quote templates
 * (Hitachi Energy, GE Vernova, Siemens Energy, Prysmian, Tesla
 * Megapack, NuScale, Cummins). They fire only on language that
 * unambiguously identifies the field — anything weaker drops to
 * section-routed tier-2 or LLM-assisted tier-3.
 *
 * Adding patterns: keep the most-specific entries first. Cite the
 * source template inline (e.g. "// Hitachi Energy QU-2024 quote
 * template") when adding so future maintainers can defend the regex.
 */
import type { ExtractedQuote, ExtractedQuoteField } from './extractQuoteFromPdf';

export interface QuoteDefinedPattern {
  field: ExtractedQuoteField;
  pattern: RegExp;
  group: number;
  transform?: (raw: string) => string | number | null;
}

export interface QuoteStringPattern {
  field: ExtractedQuoteField;
  pattern: RegExp;
  group: number;
  transform?: (raw: string) => string | null;
}

export interface QuoteSectionRoute {
  field: ExtractedQuoteField;
  heading: RegExp;
  body: RegExp;
  group: number;
  transform?: (raw: string) => string | number | null;
}

const toFloat = (raw: string): number | null => {
  const cleaned = raw.replace(/[, $]/g, '');
  const v = Number.parseFloat(cleaned);
  return Number.isFinite(v) ? v : null;
};

const toInt = (raw: string): number | null => {
  const v = Number.parseInt(raw.replace(/[, ]/g, ''), 10);
  return Number.isFinite(v) ? v : null;
};

const normalizeFreight = (raw: string): string => {
  const upper = raw.trim().toUpperCase();
  // Common Incoterms surface in quote PDFs.
  if (/\bFOB\b/.test(upper)) return 'FOB';
  if (/\bDDP\b/.test(upper)) return 'DDP';
  if (/\bCIF\b/.test(upper)) return 'CIF';
  if (/\bEXW\b/.test(upper)) return 'EXW';
  if (/\bCFR\b/.test(upper)) return 'CFR';
  if (/\bDAP\b/.test(upper)) return 'DAP';
  return upper;
};

/**
 * Numeric / structured patterns. First-match wins per field.
 */
export const QUOTE_DEFINED_PATTERNS: QuoteDefinedPattern[] = [
  // Unit price — "Unit Price: $4,500,000" / "Price (each): USD 4500000" /
  // "Net Price per unit $1,200,000"
  {
    field: 'unitCostUsd',
    pattern:
      /(?:unit\s+price|price\s+per\s+unit|net\s+price\s+per\s+unit|unit\s+cost|price\s+\(each\))\s*[:\-]?\s*(?:USD\s*)?\$?\s*([0-9][0-9,]*(?:\.\d{1,2})?)/i,
    group: 1,
    transform: toFloat,
  },
  // Total price — fallback "Total Price: $X" used when item quantity = 1.
  // Conservative — only fires when "unit" did NOT already match.
  {
    field: 'unitCostUsd',
    pattern:
      /(?:total\s+price|total\s+cost|grand\s+total|extended\s+price)\s*[:\-]?\s*(?:USD\s*)?\$?\s*([0-9][0-9,]*(?:\.\d{1,2})?)/i,
    group: 1,
    transform: toFloat,
  },
  // Lead time — "Lead time: 24 months" / "Delivery: 18-24 months ARO"
  {
    field: 'leadTimeMonths',
    pattern:
      /(?:lead\s+time|delivery(?:\s+lead)?|estimated\s+delivery)\s*[:\-]?\s*(?:approximately\s+)?(\d{1,2})(?:\s*[-–to]+\s*\d{1,2})?\s*(?:mo|months?)/i,
    group: 1,
    transform: toInt,
  },
  // Validity — "Quote valid for 30 days" / "Validity: 60 days from issue"
  {
    field: 'validityDays',
    pattern:
      /(?:valid(?:ity)?(?:\s+for)?|offer\s+valid\s+for|quote\s+valid\s+for|firm\s+for)\s*[:\-]?\s*(\d{1,3})\s*(?:days?|day)/i,
    group: 1,
    transform: toInt,
  },
];

/**
 * String-valued patterns (payment terms, freight terms, currency).
 */
export const QUOTE_STRING_PATTERNS: QuoteStringPattern[] = [
  // Payment terms — "Payment terms: Net 30" / "Terms of Payment: 30% advance,
  // 60% on delivery, 10% on commissioning"
  {
    field: 'paymentTerms',
    pattern:
      /(?:payment\s+terms?|terms?\s+of\s+payment)\s*[:\-]\s*([^\n\r]{3,160})/i,
    group: 1,
    transform: (raw: string): string => raw.trim().replace(/[\.,;]\s*$/, ''),
  },
  // Freight / Incoterm — "Delivery terms: FOB Origin" / "Incoterm: DDP"
  // / "Shipping: CIF Port of Houston". The label is case-insensitive
  // (`/i`) but Incoterm codes are case-sensitive uppercase ASCII (FOB,
  // DDP, CIF, EXW, CFR, DAP) — narrowing the capture to the union of
  // known codes both rules out false positives and keeps the captured
  // group on the code alone.
  {
    field: 'freightTerms',
    pattern:
      /(?:incoterms?|delivery\s+terms?|shipping(?:\s+terms?)?|freight\s+terms?)\s*[:\-]?\s*\b(FOB|DDP|CIF|EXW|CFR|DAP)\b/i,
    group: 1,
    transform: normalizeFreight,
  },
  // Currency — "Currency: USD" / "All prices in EUR". Label is
  // case-insensitive; the ISO-4217 code itself is captured uppercase
  // (matched as `/i`, normalized to upper in the transform).
  {
    field: 'currency',
    pattern:
      /(?:currency|all\s+prices\s+(?:in|are)|prices\s+in)\s*[:\-]?\s*([A-Z]{3})\b/i,
    group: 1,
    transform: (raw: string): string => raw.trim().toUpperCase(),
  },
];

/**
 * Section-routed fallbacks for vendor quotes whose layout buries the
 * key fields inside named clauses (typical of Hitachi/Siemens
 * energy-equipment templates).
 */
export const QUOTE_SECTION_ROUTES: QuoteSectionRoute[] = [
  {
    field: 'leadTimeMonths',
    heading:
      /(?:^|\n)\s*(?:section|article|§|chapter)\s+(?:\d+|[IVX]+)\s*[:\-—]?\s*delivery(?:\s+&?\s*\s*schedule)?/i,
    body: /(\d{1,2})\s*(?:mo|months?)\s*(?:from|after)?\s*(?:order|aro|po)?/i,
    group: 1,
    transform: toInt,
  },
  {
    field: 'paymentTerms',
    heading:
      /(?:^|\n)\s*(?:section|article|§)\s+(?:\d+|[IVX]+)\s*[:\-—]?\s*payment/i,
    body: /([^\n\r]{8,160})/,
    group: 1,
    transform: (raw: string): string => raw.trim().replace(/[\.,;]\s*$/, ''),
  },
];

/** Exposed for tests. */
export const __test__ = { toFloat, toInt, normalizeFreight };
